import React from 'react';
import PropTypes from 'prop-types';
import AbsolutePosImgs from '../_pages/AbsolutePosImgs/AbsolutePosImgs';
import { artHasCompositeSlots } from '../_pages/AbsolutePosImgs/packageLayouts';
import buyAllCardModel from './buyAllCardModel';
import { money } from '../../utils/offerPricing';

/**
 * The elementary "Download all my photos" (Buy All Digital) card (issue #5218
 * item 6, absorbing #5217). Replaces rendering the option's `buy_all_banner`
 * PNG as the WHOLE card -- that art carried "20% OFF / $39.95" baked in, which
 * kept advertising a dead tier.
 *
 * The art is now the backdrop only. Over it: the student's pose on a phone and
 * a magnet mock (the `buyAllTile` layout through the ONE compositor,
 * AbsolutePosImgs) -- but ONLY on art drawn with empty placeholders for it, since
 * those coordinates are measured against that one banner and land on live copy
 * anywhere else (artHasCompositeSlots) -- an offer ribbon, and a bottom strip with the REAL
 * price and -- only while a % tier is live -- the discountedPrice()
 * strike-through. All copy comes from buyAllCardModel, so the card reads the
 * same over today's baked-in art and the designer's price-free replacement:
 * the strip covers the lower 32% of the tile, where the old art's baked price
 * AND its BUY NOW button sit (the 22% first cut left the baked "$39.95" peeking),
 * so a family on days 0-14 still sees "$49.94 -> $39.95 / 20% OFF" from the
 * LIVE offer rather than from paint.
 *
 * Tailwind only (it lives in the Tailwind gallery); self-contained so the older
 * favourites screen can host it too.
 */
export default function BuyAllCard({
  option, offer, onClick, label, customer, packageId,
}) {
  const model = buyAllCardModel({ option, offer });
  const art = option?.buyAllBannerUrl;
  // The composite belongs to art drawn WITH two empty print placeholders. Every other
  // buy-all banner is already complete, so the cutouts would land on top of whatever it
  // draws there -- on the past-images banner, across its own "All Digital Package /
  // Download All My Photos" line. See artHasCompositeSlots.
  const showComposite = artHasCompositeSlots(art);

  return (
    <div className="relative w-full h-full bg-shoob-blue" data-testid="buy-all-card">
      {art && <img src={art} alt="" className="block w-full h-full object-cover" />}

      {/* The composite: the student dropped into the art's two empty print placeholders. */}
      {showComposite && (
        <div className="absolute inset-0 pointer-events-none">
          {customer && packageId && AbsolutePosImgs(customer, packageId, option, {
            layoutKey: 'buyAllTile',
          })}
        </div>
      )}

      {model.ribbon && !model.freeMagnets && (
        <span className="absolute top-2 left-2 z-10 rounded-md bg-red-600 px-2 py-1 text-[11px] font-extrabold uppercase tracking-wide text-white shadow">
          {model.ribbon}
        </span>
      )}

      {/* CTA panel. Covers the art's baked price line + BUY NOW (the bottom 26%
          of the 4:5 art) AND extends 48px below the frame, claiming the tile's
          empty caption row (-bottom-12 = the gallery's h-12 row; the host drops
          overflow-hidden for card tiles so this isn't clipped). That head room
          is what lets the price and the pill CTA breathe instead of cramming
          into a strip. */}
      <div className="absolute inset-x-0 top-[74%] -bottom-12 z-10 flex flex-col items-center justify-center gap-1.5 rounded-b-lg bg-white px-3">
        <span className="flex items-baseline gap-2 whitespace-nowrap leading-none">
          {model.wasPrice != null && (
            <s className="text-sm text-gray-400">{money(model.wasPrice)}</s>
          )}
          {model.nowPrice != null && (
            <span className="text-xl font-extrabold text-gray-900">{money(model.nowPrice)}</span>
          )}
        </span>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className="w-full max-w-[250px] rounded-full border-0 bg-shoob-blue px-4 py-1.5 text-center text-sm font-extrabold leading-snug text-white shadow-md hover:brightness-110 active:scale-[0.98]"
        >
          <span className="block">Download All</span>
          <span className="block">My Photos</span>
        </button>
      </div>
    </div>
  );
}

BuyAllCard.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    buyAllBannerUrl: PropTypes.string,
  }).isRequired,
  offer: PropTypes.shape({
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string,
  // The compositor's inputs (AbsolutePosImgs). Omit either to skip the composite.
  // eslint-disable-next-line react/forbid-prop-types
  customer: PropTypes.object,
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BuyAllCard.defaultProps = {
  offer: null,
  label: 'Download All My Photos',
  customer: null,
  packageId: null,
};
